import { set, get, getOr, has, omit } from 'lodash/fp';
import createReducer from '../../../../helpers/createReducer';
import {
  setOrderAdjustment,
  clearOrderAdjustment,
  addItemAdjustment,
  updateItemAdjustment,
  deleteItemAdjustment
} from '../../actions/orderAdjustment';
import createOrderItemKey from '../../../../helpers/pos/createOrderItemKey';
import setLocalOrderAdjustment from '../../../../helpers/pos/setLocalOrderAdjustment';
import { PackageType } from '../../../../constants/inventory/InventoryTypes';

/**
 * state format:
 *   add: {
 *     {itemId}_{locationId}_{zoneId}: {quantity} // for item type
 *     OR
 *     {itemId}_{locationId}: {quantity} // for package type
 *   },
 *   update: {
 *     {itemId}_{locationId}_{zoneId}: {id, quantity} // for item type
 *     OR
 *     {itemId}_{locationId}: {id, quantity} // for package type
 *   },
 *   delete: {
 *     {itemId}_{locationId}_{zoneId}: {orderItem} // for item type
 *     OR
 *     {itemId}_{locationId}: {orderItem} // for package type
 *   }
 */
export const initialState = {
  add: {},
  update: {},
  delete: {}
};

const composeNewOrderItem = (posOrderId, itemAvailability, quantity) => {
  const { type, item, location } = itemAvailability;
  const zone = get('zone', itemAvailability);
  const meta =
    type === PackageType ? { item, location } : { item, location, zone };
  return {
    posOrderId,
    quantity,
    inventoryItemId: item.id,
    inventoryLocationId: location.id,
    inventoryLocationZoneId: getOr(null, 'id', zone),
    meta
  };
};

const setOrderAdjustmentHandler = action => ({
  [action.toString()]: (_, { payload: orderAdjustment }) => orderAdjustment
});

const clearOrderAdjustmentHandler = action => ({
  [action.toString()]: (_, { payload: orderId }) => {
    setLocalOrderAdjustment(orderId, initialState);
    return initialState;
  }
});

const addItemHandler = action => ({
  [action.toString()]: (state, { payload }) => {
    const { orderId, itemAvailability, quantity } = payload;
    const newOrderItem = composeNewOrderItem(
      orderId,
      itemAvailability,
      quantity
    );
    const itemKey = createOrderItemKey(newOrderItem);
    const deletedItem = get(`delete.${itemKey}`, state);

    const updatedState = deletedItem
      ? omit(`delete.${itemKey}`, state)
      : set('add', { [itemKey]: newOrderItem, ...state.add }, state);

    setLocalOrderAdjustment(orderId, updatedState);

    return updatedState;
  }
});

const updateItemHandler = action => ({
  [action.toString()]: (state, { payload: { orderItem, quantity } }) => {
    const isExisting = has('id', orderItem);
    const itemKey = createOrderItemKey(orderItem);
    const updatedState = isExisting
      ? set(`update.${itemKey}`, { id: orderItem.id, quantity }, state)
      : set(`add.${itemKey}.quantity`, quantity, state);

    setLocalOrderAdjustment(orderItem.posOrderId, updatedState);

    return updatedState;
  }
});

const deleteItemHandler = action => ({
  [action.toString()]: (state, { payload: orderItem }) => {
    const isExisting = has('id', orderItem);
    const itemKey = createOrderItemKey(orderItem);

    const updatedState = isExisting
      ? {
          ...state,
          update: omit(itemKey, state.update),
          delete: set(itemKey, orderItem, state.delete)
        }
      : omit(`add.${itemKey}`, state);

    setLocalOrderAdjustment(orderItem.posOrderId, updatedState);

    return updatedState;
  }
});

export default createReducer(initialState, {
  ...setOrderAdjustmentHandler(setOrderAdjustment),
  ...clearOrderAdjustmentHandler(clearOrderAdjustment),
  ...addItemHandler(addItemAdjustment),
  ...updateItemHandler(updateItemAdjustment),
  ...deleteItemHandler(deleteItemAdjustment)
});
